import { LiveKitRoom, type LocalUserChoices } from '@livekit/components-react'
import { DisconnectReason } from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { exchangeConnect, exchangePrejoin, exchangeReconnect, JoinError } from './api'
import { newFailureId, report } from './diagnostics'
import { joinOutcomeFor } from './joinOutcome'
import { resumeFailureAction } from './resumeOutcome'
import { Classroom } from './Classroom'
import { reportPermissions } from './MediaReporter'
import { Prejoin } from './Prejoin'
import { t } from './i18n'
import {
  disconnectReasonName,
  isNetworkDisconnect,
  nextNetworkResumeDelay,
} from './networkResume'
import { connectOptionsFor, roomOptionsFor } from './room-options'
import { rejoinMedia } from './rejoinMedia'
import { MovedScreen } from './MovedScreen'
import {
  clearReconnect,
  loadMoved,
  loadReconnect,
  rememberChoices,
  rememberMoved,
  saveReconnect,
  type ReconnectState,
} from './session-store'
import { StatusScreen } from './StatusScreen'
import { isRetryable, retryDelaySeconds, type ConnectData, type PrejoinData } from './types'
import type { ClassMoved } from './useClassMoved'

type Phase =
  | { kind: 'loading' }
  | { kind: 'prejoin'; context: PrejoinData }
  | { kind: 'connected'; details: ConnectData; choices: LocalUserChoices }
  | { kind: 'left'; canRejoin: boolean; reconnectFailed?: boolean }
  | { kind: 'evicted' }
  /** The teacher removed this student; their link is revoked. Terminal. */
  | { kind: 'removed' }
  /** The class fell back to Zoom mid-lesson — see MovedScreen. */
  | { kind: 'moved'; url: string; host: boolean }
  /** A Ghost reached the room before a teacher opened it — auto-retrying (HTTP 425). */
  | { kind: 'waiting' }
  | { kind: 'error'; error: JoinError; failureId: string }

/**
 * Stores the credential and rotates the visible URL.
 *
 * replaceState (not a redirect, and not pushState) keeps the fragment out of
 * history entries the user can go "back" into, while making a plain reload use
 * the fresh code rather than the one just spent.
 */
/**
 * What the student last chose for their own microphone and camera.
 *
 * Honors stored choices; both mic and camera default to ON when nothing is saved,
 * so a reconnect after a drop skips the manual unmute/camera step.
 */
function rejoinChoices(sessionId: number | null): LocalUserChoices {
  const saved = loadReconnect(sessionId ?? undefined)
  return rejoinMedia(saved ?? undefined) as LocalUserChoices
}

function persistCredential(details: ConnectData): void {
  saveReconnect({
    reconnectToken: details.reconnect_token,
    sessionId: details.session.id,
    expiresAt: new Date(details.session.end_at).getTime() + GRACE_MS,
    code: details.next_code ?? undefined,
  })

  if (details.next_code) {
    try {
      window.history.replaceState(null, '', `#/join/${details.next_code}`)
    } catch {
      /* history unavailable — the stored credential still covers recovery */
    }
  }
}

/**
 * Mirrors LIVEKIT_JOIN_GRACE_MINUTES server-side. Only used to prune local
 * entries; the server remains the authority on whether a credential still works.
 */
const GRACE_MS = 15 * 60 * 1000

/**
 * A Ghost that reaches the room before a teacher has opened it gets HTTP 425:
 * the room (and its auto-egress recording) is created by the first real
 * participant to connect, never by a hidden observer. So the observer waits and
 * re-attempts the SAME exchange on a fixed cadence, landing in the instant the
 * class opens without touching anything. Bounded — a room that never opens must
 * not retry forever (30 × 15s ≈ 7.5 minutes, then a terminal screen).
 */
const WAITING_RETRY_MS = 15_000
const WAITING_MAX_ATTEMPTS = 30

export function JoinPage(props: { code: string | null; savedReconnect: ReconnectState | null }) {
  const { code, savedReconnect } = props
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })

  const fail = useCallback(
    (error: unknown) => {
      const joinError = error instanceof JoinError ? error : new JoinError('unknown', 'Something went wrong')
      // A quotable reference is only useful if the SAME id reaches our logs —
      // showing one we never reported would send support looking for nothing.
      const failureId = newFailureId()
      const outcome = joinOutcomeFor(joinError.kind)
      report(credentialRef.current, {
        stage: outcome.stage,
        outcome: outcome.outcome,
        errorKind: outcome.errorKind ?? joinError.kind,
        failureId,
      })
      setPhase({ kind: 'error', error: joinError, failureId })
    },
    [],
  )

  // How many times the waiting loop has re-attempted, and what to re-attempt.
  // The retry closure is held in a ref (not phase state) so re-arming it never
  // re-runs the timer effect, and so the counter survives the phase churn each
  // failed attempt produces. Both live across the whole mount deliberately.
  const waitingAttemptsRef = useRef(0)
  const waitingRetryRef = useRef<(() => void) | null>(null)

  // Enter the "class not open yet" wait. Whoever caught the 425 supplies the
  // exact retry for the phase that failed (connect, reconnect, or prejoin), so
  // the loop re-runs that same exchange rather than restarting from scratch.
  const enterWaiting = useCallback((retry: () => void) => {
    waitingRetryRef.current = retry
    // A fresh object each entry so the timer effect re-arms on every attempt.
    setPhase({ kind: 'waiting' })
  }, [])

  const connectWithReconnect = useCallback(
    async (reconnectToken: string, sessionId?: number) => {
      try {
        const details = await exchangeReconnect(reconnectToken)
        // A fresh budget for any FUTURE wait: this episode is over, so a later
        // reconnect that has to wait again starts its own count, not a spent one.
        waitingAttemptsRef.current = 0
        resumeCountRef.current = 0
        setPhase({
          kind: 'connected',
          details,
          choices: rejoinChoices(details.session.id),
        })
      } catch (error) {
        const joinError = error instanceof JoinError ? error : new JoinError('unknown', 'Something went wrong')
        const action = resumeFailureAction(joinError.kind)
        if (action === 'wait') {
          enterWaiting(() => void connectWithReconnect(reconnectToken, sessionId))
          return
        }
        if (action === 'keep_and_rejoin') {
          report(credentialRef.current, { stage: 'left', errorKind: `resume_failed_${joinError.kind}` })
          setPhase({ kind: 'left', canRejoin: true, reconnectFailed: true })
          return
        }
        // Clear ONLY this class's credential, never the whole device: a shared
        // family tablet may hold a sibling's live reconnect token for a
        // different class, and one flaky recover must not wipe it. A no-arg
        // clear used to nuke every entry under the prefix.
        if (sessionId != null) clearReconnect(sessionId)
        fail(joinError)
      }
    },
    [fail, enterWaiting],
  )

  // Retries the whole boot in place. Never offer "reload": boot already
  // scrubbed the code from the fragment, so navigating loses the credential.
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => {
    setPhase({ kind: 'loading' })
    setAttempt((value) => value + 1)
  }, [])

  // Boot: a fresh code goes through prejoin; a refresh/reopen with saved
  // reconnect state skips straight to connecting.
  useEffect(() => {
    let cancelled = false
    async function boot() {
      if (code) {
        try {
          const context = await exchangePrejoin(code)
          if (!cancelled) setPhase({ kind: 'prejoin', context })
        } catch (error) {
          if (cancelled) return
          // A rotated or stale fragment code must not strand a student who still
          // holds a live reconnect credential for this class — that token is
          // exactly the "tab reopened, code spent" recovery path. Spend it once
          // before failing; only surface the error if there is nothing to fall
          // back to.
          if (savedReconnect) {
            await connectWithReconnect(savedReconnect.reconnectToken, savedReconnect.sessionId)
          } else if (error instanceof JoinError && error.kind === 'waiting_for_class') {
            // Prejoin does not create the room, so a 425 here only happens if the
            // server ever gates prejoin too. Re-run the whole boot on the loop.
            enterWaiting(() => {
              setPhase({ kind: 'loading' })
              setAttempt((value) => value + 1)
            })
          } else {
            fail(error)
          }
        }
      } else if (savedReconnect) {
        await connectWithReconnect(savedReconnect.reconnectToken, savedReconnect.sessionId)
      }
    }
    void boot()
    return () => {
      cancelled = true
    }
    // `attempt` re-runs boot for the in-place retry.
  }, [code, savedReconnect, connectWithReconnect, fail, attempt, enterWaiting])

  // Credential used for telemetry reports (the code before connect, the
  // reconnect token after) — never a new secret, just what we already hold.
  const credential = useMemo(
    () => ({ code, reconnectToken: savedReconnect?.reconnectToken ?? null }),
    [code, savedReconnect?.reconnectToken],
  )
  // Ref so `fail` (created before the first render completes) can report
  // without being recreated on every credential change.
  const credentialRef = useRef(credential)
  credentialRef.current = credential

  // Network-shaped disconnects get three delayed token exchanges, then the
  // manual rejoin screen. Not a boolean: a second drop after a successful
  // resume must get a fresh budget (reset on connected).
  const resumeCountRef = useRef(0)
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Guards double-submit: the code is single-use, so a second in-flight
  // connect would consume-race the first and overwrite success with an error.
  const connectingRef = useRef(false)
  // Which class this tab is in. A device can legitimately hold credentials for
  // back-to-back lessons, so every lookup is scoped rather than "the only one".
  const sessionIdRef = useRef<number | null>(savedReconnect?.sessionId ?? null)
  // A ref cannot drive the button label, so the visible state is separate: the
  // ref prevents the double-submit, this shows the user their tap registered.
  const [joining, setJoining] = useState(false)

  const handlePrejoinSubmit = useCallback(
    async (choices: LocalUserChoices) => {
      if (!code || connectingRef.current) return
      connectingRef.current = true
      setJoining(true)
      try {
        // connect phase: consumes the one-time code, creates the room (this is
        // where auto-egress recording starts server-side) and mints the token.
        void reportPermissions(credentialRef.current)
        const details = await exchangeConnect(code)
        sessionIdRef.current = details.session.id
        persistCredential(details)
        rememberChoices(details.session.id, choices)
        // The wait (if there was one) is over — reset the budget for any later one.
        waitingAttemptsRef.current = 0
        setPhase({ kind: 'connected', details, choices })
        // Deliberately left true on success: the code is consumed, so a
        // trailing click during the prejoin→room transition must not fire a
        // second connect that errors and overwrites the connected phase.
      } catch (error) {
        // 425: the room is not open yet (a Ghost ahead of the teacher). The code
        // was NOT consumed — connect creates the room before consuming — so retry
        // the SAME connect (same code, same forced-off choices) until it opens.
        if (error instanceof JoinError && error.kind === 'waiting_for_class') {
          connectingRef.current = false
          setJoining(false)
          enterWaiting(() => void handlePrejoinSubmit(choices))
          return
        }
        fail(error)
        connectingRef.current = false
        setJoining(false)
      }
    },
    [code, fail, enterWaiting],
  )

  /**
   * The class has moved to Zoom. Stashed BEFORE the state change: the eviction
   * that follows the announce is a second or two behind, and a remount in
   * between would otherwise lose the only URL the class has.
   */
  const handleMoved = useCallback((moved: ClassMoved) => {
    const sessionId = sessionIdRef.current
    if (sessionId != null) rememberMoved(sessionId, moved.url, moved.host)
    setPhase({ kind: 'moved', url: moved.url, host: moved.host })
  }, [])

  const handleEnded = useCallback(() => {
    fail(new JoinError('ended', 'This class has ended'))
  }, [fail])

  const handleDisconnected = useCallback(
    (reason?: DisconnectReason) => {
      const reasonName = disconnectReasonName(reason)
      report(credential, { stage: 'left', errorKind: reasonName })

      // A stashed destination outranks every branch below. Whatever tore the
      // connection down, we already know where this class went, and re-exchanging
      // only to be told the same thing costs a child several seconds of staring
      // at a spinner mid-lesson.
      const moved = loadMoved(sessionIdRef.current ?? undefined)
      if (moved) {
        setPhase({ kind: 'moved', url: moved.url, host: moved.host })
        return
      }

      if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
        // Meetings::Moderation revoked this student's credentials for this class
        // before evicting them, so every way back from here would fail and then
        // tell the child to fetch a fresh link. Drop only THIS class's saved
        // credential (a sibling's class on a shared device keeps theirs) and the
        // rotated code in the address bar, which is revoked too.
        if (sessionIdRef.current != null) clearReconnect(sessionIdRef.current)
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
        } catch {
          /* history unavailable — the server refuses the code anyway */
        }
        setPhase({ kind: 'removed' })
        return
      }

      if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        // Stable identities mean a second device replaces this one — accepted,
        // messaged product behavior (plan v5 §2.5 / D12).
        setPhase({ kind: 'evicted' })
        return
      }

      // The room being deleted under us is how ops moves a whole class to Zoom
      // (SwitchProvider evicts the room). Re-exchanging immediately surfaces
      // the real reason — the API answers 409 "moved to Zoom" — instead of
      // showing "you left the class" and sending the user in circles.
      const roomWentAway =
        reason === DisconnectReason.ROOM_DELETED ||
        reason === DisconnectReason.SERVER_SHUTDOWN
      const reconnectToken = loadReconnect(sessionIdRef.current ?? undefined)?.reconnectToken
      if (roomWentAway && reconnectToken) {
        setPhase({ kind: 'loading' })
        void connectWithReconnect(reconnectToken, sessionIdRef.current ?? undefined)
        return
      }

      // The SDK has already exhausted its own reconnect policy by the time we get
      // here. A fresh token on a new connection can still succeed where resuming
      // the old one could not, so we spend exactly one attempt on it rather than
      // dropping a child onto a dead screen mid-lesson.
      //
      // Once, and only for network-shaped reasons: an eviction or an ended class
      // must not be retried, and an unbounded retry would hammer the exchange
      // from every phone behind the same mobile network at once.
      const delay = nextNetworkResumeDelay(resumeCountRef.current)
      const recoverable = Boolean(reconnectToken && delay != null && isNetworkDisconnect(reasonName))
      if (recoverable && reconnectToken && delay != null) {
        resumeCountRef.current += 1
        report(credential, { stage: 'left', errorKind: `auto_recover_${reasonName}` })
        setPhase({ kind: 'loading' })
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
        resumeTimerRef.current = setTimeout(() => {
          void connectWithReconnect(reconnectToken, sessionIdRef.current ?? undefined)
        }, delay)
        return
      }

      setPhase({ kind: 'left', canRejoin: true })
    },
    [credential, connectWithReconnect],
  )

  useEffect(() => () => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
  }, [])

  // Drives the "class not open yet" auto-retry. One timer per waiting entry: a
  // fresh `waiting` phase is set on every failed attempt, so this re-arms cleanly
  // and never stacks. Bounded — after the budget, stop hammering and show a
  // terminal screen the observer can act on instead of a spinner that never ends.
  useEffect(() => {
    if (phase.kind !== 'waiting') return
    const timer = window.setTimeout(() => {
      waitingAttemptsRef.current += 1
      if (waitingAttemptsRef.current > WAITING_MAX_ATTEMPTS) {
        setPhase({
          kind: 'error',
          error: new JoinError('not_started', 'This class has not started yet'),
          failureId: newFailureId(),
        })
        return
      }
      waitingRetryRef.current?.()
    }, WAITING_RETRY_MS)
    return () => window.clearTimeout(timer)
  }, [phase])

  switch (phase.kind) {
    case 'loading':
      return <StatusScreen title={t('boot.preparing')} />

    case 'waiting':
      return <StatusScreen title={t('waitingClass.title')} detail={t('waitingClass.detail')} />

    case 'prejoin':
      return (
        <div data-lk-theme="default">
          <Prejoin
            context={phase.context}
            busy={joining}
            onJoin={handlePrejoinSubmit}
            credential={credential}
          />
        </div>
      )

    case 'connected':
      return (
        <div className="room-screen" data-lk-theme="default">
          <LiveKitRoom
            // LiveKitRoom renders its OWN wrapper div, so without a class here the
            // header/stage/control-bar are not flex items of .room-screen — this
            // wrapper is. That is what pushed the control bar below the fold where
            // overflow:hidden then clipped it entirely.
            className="room-shell"
            serverUrl={phase.details.server_url}
            token={phase.details.token}
            connect
            // Hard off for a Ghost regardless of stored choices: a subscribe-only
            // token cannot publish, so asking LiveKitRoom to bring up a camera or
            // mic would only fire a doomed device request on entry.
            video={phase.details.current_user.meetingRole === 'Ghost' ? false : phase.choices.videoEnabled}
            audio={phase.details.current_user.meetingRole === 'Ghost' ? false : phase.choices.audioEnabled}
            // See room-options.ts. The SDK defaults leave adaptiveStream and
            // dynacast OFF and capture at 720p, which does not survive a class of
            // twenty on a Cairo mobile network.
            options={roomOptionsFor(phase.details.current_user.meetingRole, {
              videoDeviceId: phase.choices.videoDeviceId,
              audioDeviceId: phase.choices.audioDeviceId,
            })}
            connectOptions={connectOptionsFor()}
            onConnected={() => report(credential, { stage: 'signalling_connected' })}
            // `permissions`, NOT `first_media` — matching the `onError` handler
            // below and the rule it documents: a capture failure must never be
            // filed where the funnel reads it as a media/transport problem.
            //
            // This handler was the one place still contradicting that. It cost a
            // real diagnosis on 2026-08-03: session 4340574 logged a `first_media`
            // error whose kind was LiveKit's `MediaDeviceFailure.PermissionDenied`
            // — a browser refusing a camera, sitting in the metric that is
            // supposed to mean "no remote track ever arrived". The two failures
            // need opposite responses and had become indistinguishable in
            // aggregate.
            // The second argument is the DEVICE KIND, and dropping it made this
            // report almost unusable. On 2026-08-03 two of four live sessions
            // logged `PermissionDenied` here with no way to tell whether a
            // microphone or a camera had been refused — which are different
            // problems with different responses: a refused mic means nobody can
            // hear the child, a refused camera is often survivable. Both sessions
            // still showed three room participants, but three is also what a
            // tutor, a student and the egress recorder look like when one of them
            // joined with a dead camera, so the participant count could not settle
            // it either.
            onMediaDeviceFailure={(failure, kind) =>
              report(credential, {
                stage: 'permissions',
                outcome: 'error',
                errorKind: failure ? String(failure) : 'media_device_failure',
                // Mirrors onError's marker so the two device-failure entry points
                // stay tellable apart in the data.
                permission: `source=media_device_failure,device=${kind ?? 'unknown'}`,
              })
            }
            onDisconnected={handleDisconnected}
            onError={(error) => {
              console.error('[livekit]', error)
              // A DEVICE failure arrives here too, and reporting it as
              // `signalling_connected` made tonight's second incident look like a
              // transport problem when a camera simply did not exist — we only
              // learned otherwise from the tutor's hand-picked fallback reason.
              // Capture failures are their own stage so the funnel never again
              // blames the network for a missing webcam.
              const deviceError = /^(NotAllowed|NotFound|NotReadable|OverconstrainedError|TrackStart|Permission)/.test(
                error.name,
              )
              report(credential, {
                stage: deviceError ? 'permissions' : 'signalling_connected',
                outcome: 'error',
                errorKind: error.name,
                permission: deviceError ? 'source=room_error' : undefined,
              })
            }}
          >
            <Classroom
              context={phase.details}
              credential={credential}
              intended={{
                mic: phase.details.current_user.meetingRole !== 'Ghost' && phase.choices.audioEnabled,
                cam: phase.details.current_user.meetingRole !== 'Ghost' && phase.choices.videoEnabled,
              }}
              onLeave={() => setPhase({ kind: 'left', canRejoin: true })}
              onMoved={handleMoved}
              onEnded={handleEnded}
            />
          </LiveKitRoom>
        </div>
      )

    case 'left':
      return (
        <StatusScreen title={t(phase.reconnectFailed ? 'left.reconnectFailed' : 'left.title')} detail={t('left.detail')}>
          {phase.canRejoin ? <RejoinButton onRejoin={connectWithReconnect} sessionId={sessionIdRef.current} /> : null}
        </StatusScreen>
      )

    case 'evicted':
      return (
        <StatusScreen title={t('evicted.title')} detail={t('evicted.detail')}>
          <RejoinButton onRejoin={connectWithReconnect} sessionId={sessionIdRef.current} />
        </StatusScreen>
      )

    case 'removed':
      return <StatusScreen title={t('removed.title')} detail={t('removed.detail')} />

    case 'moved':
      return <MovedScreen url={phase.url} host={phase.host} />

    case 'error':
      // A class that moved to Zoom is a DESTINATION, not an error the user can
      // do anything about. When the 409 told us where it went, show the same
      // screen the live announce produces — an apology with no way forward is
      // the exact dead end this feature exists to remove. Without a URL (an
      // older server, or a class whose Zoom room is not readable) the original
      // "open it from the iSchool app" copy still stands.
      if (phase.error.kind === 'switched' && phase.error.movedUrl) {
        return <MovedScreen url={phase.error.movedUrl} host={false} />
      }
      return <ErrorScreen error={phase.error} failureId={phase.failureId} onRetry={retry} />
  }
}

function RejoinButton(props: {
  onRejoin: (reconnectToken: string, sessionId?: number) => Promise<void>
  sessionId: number | null
}) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      className="primary-button"
      disabled={busy}
      onClick={() => {
        // Read at click time — the token may have been rotated since boot.
        const saved: ReconnectState | null = loadReconnect(props.sessionId ?? undefined)
        if (!saved) return
        setBusy(true)
        void props.onRejoin(saved.reconnectToken, saved.sessionId).finally(() => setBusy(false))
      }}
    >
      {t('left.rejoin')}
    </button>
  )
}

const ERROR_KEYS: Record<
  JoinError['kind'],
  'expired' | 'notStarted' | 'ended' | 'switched' | 'network' | 'unknown' | 'unavailable'
> = {
  expired: 'expired',
  not_started: 'notStarted',
  ended: 'ended',
  switched: 'switched',
  network: 'network',
  unavailable: 'unavailable',
  // Never rendered: 'waiting_for_class' owns the dedicated `waiting` phase, and
  // the loop's give-up path converts to 'not_started'. Present only so this map
  // stays exhaustive over JoinErrorKind. Falls back to the closest copy if reached.
  waiting_for_class: 'notStarted',
  unknown: 'unknown',
}

function ErrorScreen({
  error,
  failureId,
  onRetry,
}: {
  error: JoinError
  failureId: string
  onRetry: () => void
}) {
  const key = ERROR_KEYS[error.kind]
  const [waitSeconds, setWaitSeconds] = useState(() => retryDelaySeconds(error.kind))

  useEffect(() => {
    if (waitSeconds <= 0) return
    const timer = setTimeout(() => setWaitSeconds((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [waitSeconds])

  return (
    <StatusScreen
      title={t(`error.${key}.title` as 'error.unknown.title')}
      detail={
        error.kind === 'unknown' && error.message && error.message !== 'Something went wrong'
          ? error.message
          : t(`error.${key}.detail` as 'error.unknown.detail')
      }
    >
      {isRetryable(error.kind) ? (
        <button className="primary-button" onClick={onRetry} disabled={waitSeconds > 0}>
          {waitSeconds > 0 ? `${t('error.retry')} (${waitSeconds})` : t('error.retry')}
        </button>
      ) : null}
      <p className="failure-reference">
        {t('error.reference')}: <code>{failureId}</code>
      </p>
    </StatusScreen>
  )
}
